import { useState } from "react";
import { useTranslation } from "react-i18next";
import api from "services/api";
import { getStoredMarketingParams } from "utils/marketing";

// react-router-dom components
import { Link } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKInput from "components/MKInput";
import MKButton from "components/MKButton";

// Material Kit 2 React example components
import DefaultNavbar from "components/Navbars";
import SimpleFooter from "examples/Footers/SimpleFooter";

// Material Kit 2 React page layout routes
import routes from "routes";

// Images
import bgImage from "assets/images/bg-sign-in-basic.jpeg";

function SignUpBasic() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const clearStoredAuth = () => {
    [
      "access_token",
      "refresh_token",
      "access",
      "refresh",
      "accessToken",
      "refreshToken",
      "jwt_access",
      "jwt_refresh",
      "token",
      "jwt",
      "jwt_token",
      "auth_token",
      "user",
      "auth_persistence",
    ].forEach((key) => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (!email || !password || !confirmPassword) {
      setError(t("auth.missingSignupFields") || "请输入邮箱、密码和确认密码");
      return;
    }

    if (password !== confirmPassword) {
      setError(t("auth.passwordMismatch") || "两次输入的密码不一致");
      return;
    }

    setLoading(true);
    try {
      const { ref, promo } = getStoredMarketingParams();
      const signupPayload = {
        username: email,
        password,
        confirm_password: confirmPassword,
      };
      if (ref) signupPayload.ref = ref;
      if (promo) signupPayload.promo = promo;

      const response = await api.post("auth/signup/", signupPayload);

      const { access, refresh } = response.data || {};

      clearStoredAuth();
      sessionStorage.setItem("auth_persistence", "session");
      if (access) {
        sessionStorage.setItem("access_token", access);
      }
      if (refresh) {
        sessionStorage.setItem("refresh_token", refresh);
      }
      // 注册成功后自动登录并跳转到用户中心
      window.location.href = "/userinfo/";
    } catch (err) {
      const data = err?.response?.data || {};
      const code = data.code;
      let messageKey = "";

      switch (code) {
        case "MISSING_FIELDS":
          messageKey = "auth.missingSignupFields";
          break;
        case "PASSWORD_MISMATCH":
          messageKey = "auth.passwordMismatch";
          break;
        case "EMAIL_EXISTS":
          messageKey = "auth.emailExists";
          break;
        default:
          messageKey = "auth.signupUnknownError";
          break;
      }

      const translated = messageKey ? t(messageKey) : "";
      const fallback = data.detail || data.non_field_errors?.[0] || err?.message || "";

      setError(translated || fallback || "注册失败，请稍后重试");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DefaultNavbar routes={routes} transparent light />
      <MKBox
        position="absolute"
        top={0}
        left={0}
        zIndex={1}
        width="100%"
        minHeight="100vh"
        sx={{
          backgroundImage: ({ functions: { linearGradient, rgba }, palette: { gradients } }) =>
            `${linearGradient(
              rgba(gradients.dark.main, 0.6),
              rgba(gradients.dark.state, 0.6)
            )}, url(${bgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />
      <MKBox px={1} width="100%" height="100vh" mx="auto" position="relative" zIndex={2}>
        <Grid container spacing={1} justifyContent="center" alignItems="center" height="100%">
          <Grid item xs={11} sm={9} md={5} lg={4} xl={3}>
            <Card>
              <MKBox
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                mx={2}
                mt={-3}
                py={5}
                px={4}
                mb={1}
                textAlign="center"
              >
                <MKTypography variant="h4" fontWeight="medium" color="white" mt={1}>
                  {t("auth.signUpTitle")}
                </MKTypography>
              </MKBox>
              <MKBox pt={4} pb={3} px={3}>
                <MKBox component="form" role="form" onSubmit={handleSubmit} autoComplete="off">
                  <MKBox mb={2}>
                    <MKInput
                      type="email"
                      label={t("auth.emailLabel")}
                      fullWidth
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      id="username"
                      name="username"
                      autoComplete="username"
                      InputLabelProps={{ shrink: true }}
                    />
                  </MKBox>
                  <MKBox mb={2}>
                    <MKInput
                      type="password"
                      label={t("auth.passwordLabel")}
                      fullWidth
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      id="new-password"
                      name="password"
                      autoComplete="new-password"
                      InputLabelProps={{ shrink: true }}
                    />
                  </MKBox>
                  <MKBox mb={2}>
                    <MKInput
                      type="password"
                      label={t("auth.confirmPasswordLabel")}
                      fullWidth
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      id="confirm-password"
                      name="confirm_password"
                      autoComplete="new-password"
                      InputLabelProps={{ shrink: true }}
                    />
                  </MKBox>
                  <MKBox mt={4} mb={1}>
                    <MKButton
                      variant="gradient"
                      color="info"
                      fullWidth
                      type="submit"
                      disabled={loading}
                    >
                      {loading
                        ? t("auth.signUpButtonLoading") || t("auth.signUpButton")
                        : t("auth.signUpButton")}
                    </MKButton>
                  </MKBox>
                  {error && (
                    <MKBox mt={1}>
                      <MKTypography variant="button" color="error" textAlign="center">
                        {error}
                      </MKTypography>
                    </MKBox>
                  )}
                  <MKBox mt={3} mb={1} textAlign="center">
                    <MKTypography variant="button" color="text">
                      {t("auth.alreadyHaveAccount")}{" "}
                      <MKTypography
                        component={Link}
                        to="/signin/"
                        variant="button"
                        color="info"
                        fontWeight="medium"
                        textGradient
                      >
                        {t("auth.goToSignInLink")}
                      </MKTypography>
                    </MKTypography>
                  </MKBox>
                </MKBox>
              </MKBox>
            </Card>
          </Grid>
        </Grid>
      </MKBox>
      <MKBox width="100%" position="absolute" zIndex={2} bottom="1.625rem">
        <SimpleFooter light />
      </MKBox>
    </>
  );
}

export default SignUpBasic;
